import { useCallback, useEffect, useState } from "react";
import apiRequest from "../../../utils/apiClient";
import { clipShortlistUrl, makeVideoUrl } from "../../constants";
import { CaptionStyle, Orientation, REEL } from "../constants/reelText";

/** One clip on the shortlist. Shape mirrors transcript/shortlist.py `_card`. */
export interface ReelClip {
  id: number;
  kind: string;
  text: string;
  label: string;
  speaker: string;
  sentiment: string;
  start_in: number;
  end_in: number;
  seconds: number;
  thumbnail: string;
  /** Which model the export endpoint should read this id from. */
  model: string;
}

export interface Recipe {
  key: string;
  label: string;
  blurb: string;
}

interface ShortlistResponse {
  transcript_id: number;
  title: string;
  recipe: string;
  recipes: Recipe[];
  video_url: string;
  clips: ReelClip[];
}

interface UseShortlistResult {
  data: ShortlistResponse | null;
  loading: boolean;
  error: string;
  recipe: string;
  setRecipe: (key: string) => void;
}

export const useShortlist = (
  transcriptId: string | null,
  initialRecipe = "longest_quotes",
): UseShortlistResult => {
  const [data, setData] = useState<ShortlistResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [recipe, setRecipe] = useState(initialRecipe);

  useEffect(() => {
    if (!transcriptId) {
      setLoading(false);
      setError(REEL.NO_TRANSCRIPT);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");

    apiRequest(`${clipShortlistUrl(transcriptId)}?recipe=${encodeURIComponent(recipe)}`, {
      method: "GET",
    })
      .then(async (response) => {
        if (cancelled) return;
        if (!response.ok) {
          setError(REEL.LOAD_ERROR);
          return;
        }
        setData(await response.json());
      })
      .catch(() => {
        if (!cancelled) setError(REEL.LOAD_ERROR);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [transcriptId, recipe]);

  return { data, loading, error, recipe, setRecipe };
};

/**
 * Queue ONE export per clip, so N ticked clips become N separate videos.
 *
 * This needs no new backend: `make-video` already turns a list of ids into a video, so
 * calling it once per clip is exactly "export each separately". They go one at a time on
 * purpose — the worker renders serially, and firing twenty POSTs at once just buries it
 * while counting twenty against the user's monthly export limit anyway.
 */
export const useClipExport = () => {
  const [exporting, setExporting] = useState(false);
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(0);

  const exportClips = useCallback(
    async (
      clips: ReelClip[],
      context: {
        transcriptId: number | string;
        title: string;
        autoPost: boolean;
        burnCaptions: boolean;
        captionStyle?: CaptionStyle;
        orientation?: Orientation;
        /** Per-clip shape (the frame picked on its row); falls back to `orientation`. */
        orientationFor?: (clip: ReelClip) => Orientation;
        /** My Style's caption look + start effect as make-video fields (grid/export/myStyle.js). */
        styleFields?: Record<string, string>;
      },
    ): Promise<{ queued: number; failed: number }> => {
      setExporting(true);
      setTotal(clips.length);
      setDone(0);

      let queued = 0;
      let failed = 0;

      for (const clip of clips) {
        const body = new URLSearchParams();
        body.append("list_of_ids", String(clip.id));
        body.append("transcript_id", String(context.transcriptId));
        body.append("download_id", String(context.transcriptId));
        // A per-clip title, so twenty exports aren't twenty rows with the same name.
        body.append("title", `${context.title} — ${clip.text.slice(0, 40)}`.trim());
        body.append("search_text", "");
        body.append("model", clip.model);
        body.append("kind", "mp4");
        body.append(
          "orientation",
          context.orientationFor?.(clip) ?? context.orientation ?? "original",
        );
        // Per-export "Send to Auto". The account toggle is the master switch; this lets a
        // test batch stay home without flipping the account off.
        body.append("auto_post", context.autoPost ? "true" : "false");
        // Burned captions in the phone app's look: three words at a time, the spoken one lit
        // up in the chosen palette. Postable clips need them; a few seconds more per render.
        if (context.burnCaptions) {
          body.append("burn_captions", "true");
          body.append("caption_font_key", "sans");
          body.append("caption_size", "64");
          body.append("caption_mode", "group");
          body.append("words_per_group", "3");
          body.append("caption_style", context.captionStyle ?? "cherry");
        }
        // The saved look (font, size, highlight…) over those defaults; the start effect
        // and the logo apply with or without captions.
        Object.entries(context.styleFields ?? {}).forEach(([key, value]) => {
          if (context.burnCaptions || key === "intro" || key === "logo") body.set(key, value);
        });
        body.append("adjusted_times", "{}");
        body.append("segments", "[]");

        try {
          const response = await apiRequest(makeVideoUrl, {
            method: "POST",
            body,
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
          });
          if (response.ok) {
            queued += 1;
          } else {
            failed += 1;
          }
        } catch {
          failed += 1;
        }
        setDone((n) => n + 1);
      }

      setExporting(false);
      return { queued, failed };
    },
    [],
  );

  return { exportClips, exporting, done, total };
};
